/**
 * Complemento do produto no PDP (snippets/block-addon-picker.liquid).
 *
 * product-form.js usa dois pontos deste elemento:
 * - validate(), no envio: com data-required a escolha é obrigatória (um complemento ou
 *   "sem complemento"); sem o atributo, nada escolhido vale como "sem complemento";
 * - addNested(), logo depois de adicionar o produto principal: o complemento escolhido entra
 *   como linha aninhada (parent_line_key) na linha recém-criada, com a mesma quantidade, a
 *   variante da mesma argila e a mesma cor (propriedade do app Avis, "Cor (...)"), para o
 *   conjunto sair combinando.
 */
if (!customElements.get('addon-picker')) {
  customElements.define(
    'addon-picker',
    class AddonPicker extends HTMLElement {
      connectedCallback() {
        this.addEventListener('change', () => this.toggleError(false));
        // click (e não change): tocar de novo no complemento já escolhido reabre a foto
        this.addEventListener('click', (event) => {
          if (event.target.closest('.addon-picker__preview-close')) return this.hidePreview();
          const input = event.target.closest('.addon-picker__input');
          if (input) this.showPreview(input);
        });
      }

      // Onde não há mouse para o zoom do hover (celular e telas de toque): tocar em um
      // complemento mostra a foto grande acima do bloco por 6 segundos, com um x para fechar.
      get usesTapPreview() {
        return !window.matchMedia('(hover: hover) and (min-width: 750px)').matches;
      }

      showPreview(input) {
        clearTimeout(this.previewTimer);
        const option = input.closest('.addon-picker__option');
        const source = option && option.querySelector('.addon-picker__zoom img');
        if (!this.usesTapPreview || !source) return this.hidePreview();

        if (!this.preview) {
          this.preview = document.createElement('div');
          this.preview.className = 'addon-picker__preview';
          this.previewImage = document.createElement('img');
          this.previewImage.className = 'addon-picker__preview-image';
          this.previewImage.width = 320;
          this.previewImage.height = 320;
          const close = document.createElement('button');
          close.type = 'button';
          close.className = 'addon-picker__preview-close';
          close.setAttribute('aria-label', 'Fechar foto');
          close.textContent = '×';
          this.preview.append(this.previewImage, close);
          this.prepend(this.preview);
        }

        const name = option.querySelector('.addon-picker__name');
        this.previewImage.src = source.getAttribute('src');
        this.previewImage.alt = name ? name.textContent.trim() : '';
        this.preview.hidden = false;
        this.preview.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        this.previewTimer = setTimeout(() => this.hidePreview(), 6000);
      }

      hidePreview() {
        clearTimeout(this.previewTimer);
        if (this.preview) this.preview.hidden = true;
      }

      get selected() {
        return this.querySelector('.addon-picker__input:checked');
      }

      toggleError(show) {
        this.classList.toggle('addon-picker--error', show);
        const message = this.querySelector('.addon-picker__error');
        if (message) message.hidden = !show;
      }

      validate() {
        if (this.selected || !this.hasAttribute('data-required')) return true;

        this.toggleError(true);
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const first = this.querySelector('.addon-picker__input');
        if (first) first.focus({ preventScroll: true });
        return false;
      }

      parse(json) {
        try {
          return JSON.parse(json || '{}');
        } catch (e) {
          return {};
        }
      }

      // Variante do complemento na mesma argila da variante principal; sem correspondência,
      // a primeira disponível.
      variantFor(input, parentVariantId) {
        const variants = this.parse(input.dataset.variants);
        const argila = this.parse(this.dataset.parentArgilas)[String(parentVariantId)] || '';
        return variants[argila] || variants[Object.keys(variants)[0]];
      }

      addNested(parentLine, formData) {
        const input = this.selected;
        if (!input || !input.value || !parentLine || !parentLine.key) return Promise.resolve();

        const variantId = this.variantFor(input, parentLine.variant_id || formData.get('id'));
        if (!variantId) return Promise.resolve();

        const properties = {};
        for (const [key, value] of formData.entries()) {
          const match = /^properties\[(.+)\]$/.exec(key);
          if (match && /^cor\b/i.test(match[1]) && typeof value === 'string' && value) properties[match[1]] = value;
        }

        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        return fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: parseInt(formData.get('quantity') || '1', 10) || 1,
                parent_line_key: parentLine.key,
                properties,
              },
            ],
          }),
        }).then((response) => response.json());
      }
    }
  );
}
